import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  DisplayUserEmail,
  GalleryInvitation,
  GalleryMemberAccess,
  GalleryMemberDto,
  GalleryTenantAccessLevel,
  MemberDto,
} from '@nusign/picflow-backend-types';
import { Observable, catchError, delay, forkJoin, map, of, retry, switchMap, take, tap, throwError } from 'rxjs';
import { standardMemoryCacheExpiry } from '../consts';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { GalleryPasswordService } from './gallery-password.service';
import { GetAllService } from './get-all.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { DisplayUserEmailDto } from './visitor.service';
import { WafHttpService } from './waf-http.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryMemberService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private getAllService = inject(GetAllService);
  private wafHttpService = inject(WafHttpService);

  public getGalleryMembers(galleryId: string): Observable<GalleryMemberDto[]> {
    return this.loader
      .get(
        `gallery_members_${galleryId}`,
        () =>
          this.getAllService.getAll<GalleryMemberDto>(
            addGalleryPassword(
              `${this.config.apiEndpoint}/gallery_members?gallery=${galleryId}&expand[]=data.user`,
              this.galleryPasswordService.getPassword(galleryId)
            ),
            new HttpContext().set(IGNORE_AUTH_ERRORS, true)
          ),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return of([]);
        }),
        map(members => members.filter(m => m.gallery === galleryId))
      );
  }

  public getAllGalleryMembers(unique: boolean): Observable<GalleryMemberDto[]> {
    return this.loader
      .get(
        'gallery_members',
        () =>
          this.getAllService.getAll<GalleryMemberDto>(
            `${this.config.apiEndpoint}/gallery_members?expand[]=data.user`,
            new HttpContext().set(IGNORE_AUTH_ERRORS, true)
          ),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return of([]);
        }),
        map(members => (unique ? this.makeMembersUnique(members) : members))
      );
  }

  public getPendingGalleryMembers(galleryId: string): Observable<GalleryInvitation[]> {
    return this.loader
      .get(
        `gallery_invitations_${galleryId}`,
        () =>
          this.getAllService.getAll<GalleryInvitation>(
            `${this.config.apiEndpoint}/gallery_invitations?expand[]=data.user&gallery=${galleryId}`,
            new HttpContext().set(IGNORE_AUTH_ERRORS, true)
          ),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return of([]);
        })
      );
  }

  public getAllPendingGalleryMembers(): Observable<GalleryInvitation[]> {
    return this.loader
      .get(
        'gallery_invitations',
        () =>
          this.getAllService.getAll<GalleryInvitation>(
            `${this.config.apiEndpoint}/gallery_invitations?expand[]=data.user`,
            new HttpContext().set(IGNORE_AUTH_ERRORS, true)
          ),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return of([]);
        })
      );
  }

  public getGalleryMembersForUser(userId: string): Observable<GalleryMemberDto[]> {
    return this.getAllGalleryMembers(false).pipe(map(members => members.filter(m => m.user['id'] === userId)));
  }

  private waitTillMemberIsAdded(galleryId: string, email: string) {
    return of(undefined).pipe(
      switchMap(() => {
        return forkJoin([
          this.loader
            .forceDataRefresh<(GalleryMemberDto & { user: DisplayUserEmail })[]>(`gallery_members_${galleryId}`)
            .pipe(take(1)),
          this.loader.forceDataRefresh<GalleryInvitation[]>(`gallery_invitations_${galleryId}`).pipe(take(1)),
        ]).pipe(
          switchMap(([members, invites]) => {
            if (members.find(m => m.user.email === email)) return of(true);
            if (invites.find(i => i.email === email)) return of(true);
            return throwError(() => 'Member not added');
          })
        );
      }),
      retry({ count: 30, delay: 1000 }),
      catchError(() => of(false))
    );
  }

  public inviteMember(
    email: string,
    galleryId: string,
    accessLevel: GalleryTenantAccessLevel,
    notify: boolean,
    message: string
  ) {
    const params = {
      email,
      gallery: galleryId,
      access: accessLevel,
      message: message || undefined,
      notify: notify,
    };
    return this.wafHttpService.post(`${this.config.apiEndpoint}/gallery_invitations`, params).pipe(
      delay(1500), // waiting for the backend to auto-accept the invite
      switchMap(() => this.waitTillMemberIsAdded(galleryId, email)),
      tap(memberIsAdded => {
        if (!memberIsAdded) return;
        this.loader.forceDataRefresh('gallery_members');
        this.loader.forceDataRefresh('gallery_invitations');
      })
    );
  }

  public updateMember(memberId: string, accessLevel: GalleryMemberAccess) {
    const params = {
      access: accessLevel,
    };
    return this.http.patch(`${this.config.apiEndpoint}/gallery_members/${memberId}`, params).pipe(
      tap(() => {
        this.loader.updateFuzzy<GalleryMemberDto[]>('gallery_members', members => {
          const member = members.find(m => m.id === memberId);
          if (!member) return members;
          member.access = accessLevel;
          return members;
        });
      })
    );
  }

  public updateInvite(inviteId: string, accessLevel: GalleryMemberAccess) {
    const params = {
      access: accessLevel,
    };
    return this.http.patch(`${this.config.apiEndpoint}/gallery_invitations/${inviteId}`, params).pipe(
      map(resp => {
        this.loader.updateFuzzy<GalleryInvitation[]>('gallery_invitations', invites => {
          const invite = invites.find(i => i.id === inviteId);
          if (!invite) return invites;
          invite.access = accessLevel;
          return invites;
        });
        return resp;
      })
    );
  }

  public removeAll(galleryId: string) {
    return forkJoin([
      this.getGalleryMembers(galleryId).pipe(
        take(1),
        switchMap(members => forkJoinSafe(members.map(member => this.removeMember(member.id))))
      ),
      this.getPendingGalleryMembers(galleryId).pipe(
        take(1),
        switchMap(members => forkJoinSafe(members.map(member => this.removeInvite(member.id))))
      ),
    ]);
  }

  public removeMember(memberId: string) {
    return this.http.delete(`${this.config.apiEndpoint}/gallery_members/${memberId}`).pipe(
      tap(() => {
        this.loader.updateFuzzy<MemberDto[]>('gallery_members', members =>
          (members || []).filter(m => m.id !== memberId)
        );
      })
    );
  }

  public removeInvite(inviteId: string) {
    return this.http.delete(`${this.config.apiEndpoint}/gallery_invitations/${inviteId}`).pipe(
      tap(() => {
        this.loader.updateFuzzy<GalleryInvitation[]>('gallery_invitations', invites =>
          (invites || []).filter(m => m.id !== inviteId)
        );
      })
    );
  }

  makeMembersUnique(members: GalleryMemberDto[]) {
    const uniqueMembers: GalleryMemberDto[] = [];
    members.forEach(member => {
      const user = member.user as DisplayUserEmailDto;
      if (!uniqueMembers.find(m => m.user['id'] === user.id)) {
        uniqueMembers.push(member);
      }
    });
    return uniqueMembers;
  }

  makeInvitesUnique(invites: GalleryInvitation[]) {
    const uniqueInvites: GalleryInvitation[] = [];
    invites.forEach(invite => {
      if (!uniqueInvites.find(m => m.email === invite.email)) {
        uniqueInvites.push(invite);
      }
    });
    return uniqueInvites;
  }
}
